// A UI-agnostic survey session: paged questions (one per page, one section
// per page, or all on one page), conditional skipping, seeded randomization,
// and per-question timing (FR-11). The web runner and
// the mobile app both drive this state machine; the mobile app persists it to
// SQLite so an interrupted response can be resumed offline.
import { flowAnswers, flowPicks, questionOrder } from './randomize';
import { compareKeys, computeScore, matchingQuotas, orderResolved, presentationKey, resolve } from './resolve';
import { pipeQuestion } from './text';
import type { AnswerValue, Answers, Definition, Ending, FlowCounts, PageMode, Question, Quota, Submission } from './types';
import type { Locale } from './i18n';
import { cleanAnswers, validateQuestion } from './validate';

export interface Session {
  respondentRecordId?: string;
  submissionId: string;
  surveyCode: string;
  surveyVersion: number;
  order: string[];
  answers: Answers;
  /** Active milliseconds per question. */
  timings: Record<string, number>;
  /** First question id of the current page; null once the respondent reached the end. */
  currentId: string | null;
  startedAt: string;
  activeMs: number;
  /**
   * Open timing segment, or null while paused (tab hidden / app backgrounded).
   * On multi-question pages it follows the question the respondent last
   * interacted with (see focusQuestion).
   */
  segment: { questionId: string | null; since: number } | null;
  /** Hidden-field values captured from the link when the response started. */
  embedded?: Record<string, string>;
  /** Language the respondent chose. */
  language?: string;
}

type SessionView = Pick<Session, 'order' | 'answers'>;

interface Layout {
  def: Definition;
  /** Every resolved question in presentation order, visible or not. */
  all: Question[];
  pages: Question[][];
  ending: string | null;
}

function layout(def: Definition, s: SessionView): Layout {
  const r = resolve(def, s.answers);
  const all = orderResolved(def, s.order, r.questions);
  const shown = all.filter((q) => r.visible[q.id]);
  const mode = pageMode(def);
  let pages: Question[][];
  if (mode === 'all') pages = shown.length ? [shown] : [];
  else if (mode === 'question') pages = shown.map((q) => [q]);
  else {
    // Each loop pass of a section is its own page.
    const key = (q: Question) => {
      const title = q.sectionTitle?.trim() ?? '';
      return title ? `${title}\u0000${q.loop?.optionId ?? ''}` : '';
    };
    pages = [];
    let prev = '';
    for (const q of shown) {
      const k = key(q);
      const last = pages[pages.length - 1];
      if (last && k && k === prev) last.push(q);
      else pages.push([q]);
      prev = k;
    }
  }
  return { def, all, pages, ending: r.ending };
}

export function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // RFC 4122 v4 fallback for runtimes without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Visible question ids in presentation order (loop copies included). */
export function visibleIds(def: Definition, s: SessionView): string[] {
  return layout(def, s).pages.flat().map((q) => q.id);
}

export function pageMode(def: Definition): PageMode {
  return def.settings.pageMode ?? 'question';
}

/**
 * Visible question ids grouped into pages, in presentation order. Pages are
 * recomputed from the live answers, so a question revealed by an answer on
 * the same page appears on that page immediately.
 */
export function pages(def: Definition, s: SessionView): string[][] {
  return layout(def, s).pages.map((p) => p.map((q) => q.id));
}

/** Index of the page holding currentId; pages.length once finished. */
function pageIndex(s: Pick<Session, 'order' | 'answers' | 'currentId'>, l: Layout): number {
  const ps = l.pages;
  if (s.currentId === null) return ps.length;
  const direct = ps.findIndex((p) => p.some((q) => q.id === s.currentId));
  if (direct >= 0) return direct;
  // The current question became hidden or its loop pass went away: continue at the next page in order.
  const key = presentationKey(l.def, s.order);
  const here = key(s.currentId);
  const after = ps.findIndex((p) => compareKeys(key(p[0]!.id), here) >= 0);
  return after >= 0 ? after : ps.length;
}

/** Questions on the current page with their text piped, in presentation order. Empty once finished. */
export function currentPage(def: Definition, s: Session, locale: Locale = 'en'): Question[] {
  const l = layout(def, s);
  const page = l.pages[pageIndex(s, l)] ?? [];
  if (!page.length) return [];
  const needsScore = page.some((q) => JSON.stringify(q).includes('{{score}}'));
  const ctx = { questions: l.all, answers: s.answers, embedded: s.embedded, locale, score: needsScore ? computeScore(def, s.answers) : null };
  return page.map((q) => pipeQuestion(q, ctx));
}

/** The ending a jump rule chose, once the respondent has reached it. */
export function reachedEnding(def: Definition, s: SessionView): Ending | null {
  const id = layout(def, s).ending;
  return id ? (def.endings?.find((e) => e.id === id) ?? null) : null;
}

/** The first full quota the answers match, if any: the respondent is screened out. */
export function screenedOutBy(def: Definition, s: SessionView, fullQuotas: string[] | undefined): Quota | null {
  if (!fullQuotas?.length) return null;
  const hit = matchingQuotas(def, s.answers).find((id) => fullQuotas.includes(id));
  return hit ? (def.quotas?.find((q) => q.id === hit) ?? null) : null;
}

export function startSession(
  def: Definition,
  survey: { code: string; version: number },
  opts: { submissionId?: string; now?: number; embedded?: Record<string, string>; language?: string; flowCounts?: FlowCounts; picks?: Answers } = {},
): Session {
  const now = opts.now ?? Date.now();
  const submissionId = opts.submissionId ?? uuid();
  // Which sections this respondent sees for each randomizer; they are saved with the answers.
  // Picks the server already made for this respondent (balanced randomizers) win over local ones.
  const answers = { ...flowPicks(def, submissionId, opts.flowCounts), ...(opts.picks ? flowAnswers(def, opts.picks) : {}) };
  const order = questionOrder(def, submissionId, answers);
  const first = pages(def, { order, answers })[0]?.[0] ?? null;
  const embedded = pickEmbedded(def, opts.embedded);
  return {
    ...(embedded ? { embedded } : {}),
    ...(opts.language ? { language: opts.language } : {}),
    submissionId,
    surveyCode: survey.code,
    surveyVersion: survey.version,
    order,
    answers,
    timings: {},
    currentId: first,
    startedAt: new Date(now).toISOString(),
    activeMs: 0,
    segment: { questionId: first, since: now },
  };
}

/** Closes the open timing segment, crediting elapsed time to its question. */
function flush(s: Session, now: number): Session {
  if (!s.segment) return s;
  const elapsed = Math.max(0, now - s.segment.since);
  const timings = { ...s.timings };
  const qid = s.segment.questionId;
  if (qid) timings[qid] = (timings[qid] ?? 0) + elapsed;
  return { ...s, timings, activeMs: s.activeMs + elapsed, segment: { questionId: qid, since: now } };
}

export function pause(s: Session, now = Date.now()): Session {
  return s.segment ? { ...flush(s, now), segment: null } : s;
}

export function resume(s: Session, now = Date.now()): Session {
  return s.segment ? s : { ...s, segment: { questionId: s.currentId, since: now } };
}

function moveTo(s: Session, id: string | null, now: number): Session {
  const flushed = flush(s, now);
  return { ...flushed, currentId: id, segment: flushed.segment ? { questionId: id, since: now } : null };
}

/** Active milliseconds so far, including the open segment — drives the on-screen timer. */
export function elapsedMs(s: Session, now = Date.now()): number {
  return s.activeMs + (s.segment ? Math.max(0, now - s.segment.since) : 0);
}

/** Formats elapsed time as m:ss, or h:mm:ss past an hour. */
export function formatElapsed(ms: number): string {
  const total = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/**
 * Credits time from now on to another question on the current page. Call it
 * when the respondent focuses or answers a question on a multi-question page.
 */
export function focusQuestion(s: Session, questionId: string, now = Date.now()): Session {
  if (!s.segment || s.segment.questionId === questionId) return s;
  const flushed = flush(s, now);
  return { ...flushed, segment: { questionId, since: now } };
}

export function setAnswer(s: Session, questionId: string, value: AnswerValue): Session {
  return { ...s, answers: { ...s.answers, [questionId]: value } };
}

export interface Step {
  session: Session;
  /** First validation error on the page, or null. */
  error: string | null;
  /** Validation errors by question id for every invalid question on the page. */
  errors: Record<string, string>;
}

/** Keeps the declared hidden fields (case-insensitive names), trimmed to 500 characters. */
export function pickEmbedded(def: Definition, values: Record<string, string> | undefined): Record<string, string> | undefined {
  const names = def.settings.hiddenFields ?? [];
  if (!values || !names.length) return undefined;
  const out: Record<string, string> = {};
  for (const name of names) {
    const key = Object.keys(values).find((k) => k.toLowerCase() === name.toLowerCase());
    const v = key === undefined ? undefined : values[key];
    if (typeof v === 'string' && v.trim()) out[name] = v.trim().slice(0, 500);
  }
  return Object.keys(out).length ? out : undefined;
}

/** Validation errors for the questions on the current page. */
export function pageErrors(def: Definition, s: Session, locale: Locale = 'en'): Record<string, string> {
  const l = layout(def, s);
  const errors: Record<string, string> = {};
  for (const q of l.pages[pageIndex(s, l)] ?? []) {
    const e = validateQuestion(q, s.answers, locale);
    if (e) errors[q.id] = e;
  }
  return errors;
}

/** Validates the current page and advances to the next visible page. */
export function next(def: Definition, s: Session, now = Date.now(), locale: Locale = 'en'): Step {
  if (s.currentId === null) return { session: s, error: null, errors: {} };
  const errors = pageErrors(def, s, locale);
  const first = Object.values(errors)[0];
  if (first) return { session: s, error: first, errors };
  const l = layout(def, s);
  const idx = pageIndex(s, l);
  return { session: moveTo(s, l.pages[idx + 1]?.[0]?.id ?? null, now), error: null, errors: {} };
}

export function back(def: Definition, s: Session, now = Date.now()): Session {
  const l = layout(def, s);
  const idx = pageIndex(s, l);
  return idx > 0 ? moveTo(s, l.pages[idx - 1]![0]!.id, now) : s;
}

export interface Progress {
  /** Zero-based current page; equals total once finished. */
  index: number;
  /** Number of pages. Equal to the question count in one-question-per-page mode. */
  total: number;
  fraction: number;
  /** Number of visible questions across all pages. */
  questions: number;
}

export function progress(def: Definition, s: Session): Progress {
  const l = layout(def, s);
  const ps = l.pages;
  const index = pageIndex(s, l);
  const questions = ps.reduce((n, p) => n + p.length, 0);
  return { index, total: ps.length, fraction: ps.length ? index / ps.length : 1, questions };
}

/** Builds the API payload. Hidden and empty answers are dropped. */
export function toSubmission(def: Definition, s: Session, now = Date.now()): Submission {
  const done = pause(s, now);
  const answers = cleanAnswers(def, done.answers);
  const l = layout(def, done);
  const shownOrder = l.pages.flat().map((q) => q.id);
  const shown = new Set(shownOrder);
  const timings: Record<string, number> = {};
  for (const [id, ms] of Object.entries(done.timings)) if (shown.has(id)) timings[id] = Math.round(ms);
  return {
    clientSubmissionId: done.submissionId,
    ...(done.respondentRecordId ? { respondentRecordId: done.respondentRecordId } : {}),
    surveyVersion: done.surveyVersion,
    answers,
    timings,
    questionOrder: shownOrder,
    startedAt: done.startedAt,
    submittedAt: new Date(now).toISOString(),
    durationMs: Math.round(done.activeMs),
    ...(done.embedded ? { embedded: done.embedded } : {}),
    ...(done.language ? { language: done.language } : {}),
    ...(l.ending ? { endingId: l.ending } : {}),
  };
}

export type { Answers };
